import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import "./FAQ.scss";
import { IconAdd, IconRemove } from "assets/assetReference";

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: "What does WeBridge do in the offshore wind industry?",
    answer:
      "WeBridge is an independent advisory firm that bridges the gap between European offshore wind developers and international suppliers. We provide specialist consulting services to developers, suppliers, investors and insurers, facilitating successful collaborations across the offshore wind supply chain.",
  },
  {
    question: "What is offshore wind energy?",
    answer:
      "Offshore wind energy is electricity generated by wind turbines installed in bodies of water, typically on the continental shelf. Turbines are mounted on fixed foundations (such as monopiles or jackets) or floating platforms and connected to the onshore grid via subsea cables. Europe is the world's leading market, with the UK, Germany, the Netherlands and Denmark operating the largest installed capacity.",
  },
  {
    question:
      "What is the UK Contracts for Difference (CfD) scheme for offshore wind?",
    answer:
      "The Contracts for Difference (CfD) scheme is the UK government's primary mechanism for supporting new offshore wind projects. Developers bid in competitive allocation rounds (AR) for a guaranteed 'strike price' per megawatt-hour of electricity generated. If the market price falls below the strike price, the government pays the difference; if it rises above, the developer pays back. CfD provides long-term revenue certainty, enabling projects to secure financing.",
  },
  {
    question:
      "What is the Carbon Border Adjustment Mechanism (CBAM) and how does it affect offshore wind?",
    answer:
      "The Carbon Border Adjustment Mechanism (CBAM) is an EU regulation that places a carbon price on imports of carbon-intensive goods — including steel, aluminium, cement and electricity — entering the European Union. For the offshore wind industry, CBAM means that importing foundations, towers, and other steel-intensive components from non-EU countries now requires purchasing CBAM certificates equivalent to the EU carbon price, increasing procurement costs and adding compliance obligations for developers and manufacturers.",
  },
  {
    question:
      "How does WeBridge help international suppliers enter the European offshore wind market?",
    answer:
      "We offer comprehensive business development services tailored to each supplier's specific needs, including market analysis, strategic positioning, introductions to European developers and EPCI contractors, regulatory guidance and ongoing support to ensure a successful market entry and sustainable growth in the European offshore wind sector.",
  },
  {
    question: "What services does WeBridge offer to offshore wind developers?",
    answer:
      "We provide complementary specialist services to European offshore wind developers, including supply chain optimisation, international supplier sourcing and qualification, commercial and technical due diligence, and strategic advisory — helping bridge demand and supply gaps in the European market.",
  },
  {
    question: "What is offshore wind supply chain management?",
    answer:
      "Offshore wind supply chain management is the coordination of sourcing, manufacturing, logistics and installation of all components needed to build and operate an offshore wind farm — from turbines and foundations to subsea cables and installation vessels. Effective supply chain management addresses capacity bottlenecks, quality assurance, lead times and cost optimisation across a global network of manufacturers and service providers.",
  },
  {
    question:
      "Does WeBridge provide advisory services for offshore wind investors?",
    answer:
      "Yes. WeBridge offers a range of services to both debt and equity investors in the offshore wind industry, designed to support investing and lending decisions, risk assessment, due diligence processes and ongoing portfolio monitoring throughout the project lifecycle.",
  },
  {
    question: "What regions does WeBridge cover for offshore wind consulting?",
    answer:
      "WeBridge primarily focuses on the European offshore wind market, including the UK, Germany, the Netherlands, France, Denmark, Poland and other emerging markets. We also work with international suppliers from China and other global supply chain hubs looking to enter or expand in Europe.",
  },
  {
    question:
      "How can I stay updated with the latest offshore wind market developments?",
    answer:
      "WeBridge publishes regular insights including in-depth articles, the Global Offshore Wind Weekly newsletter and event coverage. Visit our Insights page to explore our latest analysis, or subscribe to our newsletter to receive updates directly.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <div className="faq-section">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <p className="tag">COMMON QUESTIONS</p>
      <h2 className="subheading">Frequently Asked Questions</h2>

      <div className="faq-list">
        {faqData.map((item, i) => (
          <div
            key={i}
            className={`faq-item ${openIndex === i ? "faq-item--open" : ""}`}
          >
            <button
              type="button"
              className="faq-question"
              onClick={() => toggle(i)}
              aria-expanded={openIndex === i ? "true" : "false"}
            >
              <span>{item.question}</span>
              {openIndex === i ? <IconRemove /> : <IconAdd />}
            </button>
            <div className="faq-answer" role="region" hidden={openIndex !== i}>
              <p>{item.answer}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
